/*
 * design-entry.js — ONE builder for every design-entry URL on the Vivid platform.
 *
 * Both journeys end on the same pages, so both must speak the same URL contract:
 *   Path A  product page  → "Explore design ideas" (vivid-buy-cards.liquid __vvGalleryUrl)
 *   Path B  /pages/design-online card router (vv-design-online.js)
 *   …→ gallery.html → designer.html / studio (?vvcc=blank|upload, ?vvstyle=<key>)
 *   …← Back / Cancel / recovery (?needsize=1, /pages/design-online)
 *
 * THE CONTRACT (query names are the API — never rename without a migration):
 *   product       Shopify product id (numeric string)           — required on gallery/designer
 *   gallery       Shopify product HANDLE (the routing key for F7 library resolution,
 *                 the server-side config requirement and every recovery redirect)
 *   variant       Shopify variant id (numeric string)           — the explicit customer choice
 *   variantTitle  that variant's title (the server resolves the canvas size from it)
 *   from=gallery  designer only: Back returns to the gallery, not to document.referrer
 *   vvcc=blank|upload + vvfrom=gallery   studio deep entries (theme vivid-buy-cards / cc-app)
 *   vvstyle=<key> + vvfrom=gallery       ready-made design deep entry (cc-app GALLERY DEEP LINK)
 *   needsize=1    product page: a design-affecting product arrived with no explicit size
 *   designOnline=1 product page: continuation banner for the Design Online journey
 *   vvmaster      studio entries only: the master page size ("WxH" mm, from the server's product context)
 *                 the studio uses to pick the blank starter / ready-made design of the RIGHT size on its
 *                 rail (names carry the master size: "FeatherFlags-DYO-600x2213.8"); never a canvas override.
 *   cfg           PRODUCT_PAGE (calculator) products only: the product page's calculator
 *                 configuration (base64url JSON of its line properties — Size/Width/Height/…),
 *                 opaque here; the server (calculator-config.server.js) is the only reader.
 *                 These products have one "Default Title" variant, so `cfg` IS their configuration.
 *
 * Geometry (w/h/faces/artworkMode) is deliberately NOT part of the URL for variant products: the
 * server (resolveProductDims) is the only authority on canvas size, from product+variantTitle (or
 * product+cfg for calculator products).
 *
 * Plain script (UMD-ish): window.VVDesignEntry in a browser, module.exports in node.
 * Served by the create app at /lib/design-entry.js and mirrored byte-for-byte as the
 * theme asset vv-design-entry.js (test/design-entry.test.mjs enforces the mirror).
 */
(function (root) {
  "use strict";

  var TOOL = "https://create.vividads.com.au";
  var STOREFRONT = "https://www.vividads.com.au";

  function digits(v) { var s = String(v == null ? "" : v).replace(/\D/g, ""); return s; }
  function handle(v) { return String(v == null ? "" : v).trim().toLowerCase().replace(/[^a-z0-9-]/g, ""); }
  function enc(v) { return encodeURIComponent(String(v)); }
  function base(given, dflt) { var b = String(given || dflt); return b.replace(/\/+$/, ""); }
  // the calculator configuration token is base64url: anything else is dropped, never forwarded
  function sizeKey(v) { var s = String(v == null ? "" : v).trim().toLowerCase(); return /^\d+(\.\d+)?x\d+(\.\d+)?$/.test(s) ? s : ""; }
  function cfgToken(v) { var s = String(v == null ? "" : v).trim(); return /^[A-Za-z0-9_-]{1,6000}$/.test(s) ? s : ""; }

  // Ordered query builder — order is part of the contract so URLs are byte-stable.
  function query(pairs) {
    var q = [];
    for (var i = 0; i < pairs.length; i++) {
      var k = pairs[i][0], v = pairs[i][1];
      if (v == null || v === "") continue;
      q.push(k + "=" + enc(v));
    }
    return q.length ? "?" + q.join("&") : "";
  }

  // Normalise the entry descriptor every builder takes.
  function ctx(o) {
    o = o || {};
    return {
      tool: base(o.tool, TOOL),
      storefront: base(o.storefront, STOREFRONT),
      productId: digits(o.productId || o.product),
      handle: handle(o.handle || o.gallery),
      variantId: digits(o.variantId || o.variant),
      variantTitle: o.variantTitle == null ? "" : String(o.variantTitle),
      cfg: cfgToken(o.cfg),
      master: sizeKey(o.master)
    };
  }

  /* gallery.html — ready-made designs + the four ways in (Path A and Path B both land here) */
  function galleryUrl(o) {
    var c = ctx(o);
    return c.tool + "/gallery.html" + query([["product", c.productId], ["gallery", c.handle], ["variant", c.variantId], ["variantTitle", c.variantTitle], ["cfg", c.cfg]]);
  }
  /* designer.html — Design with AI. from=gallery ⇒ Back returns to galleryUrl(same ctx). */
  function designerUrl(o) {
    var c = ctx(o);
    return c.tool + "/designer.html" + query([["product", c.productId], ["gallery", c.handle], ["variant", c.variantId], ["variantTitle", c.variantTitle], ["cfg", c.cfg], ["from", "gallery"]]);
  }
  /* product page + studio deep entry: way = "blank" | "upload". vvfrom=gallery ⇒ studio Back/Close → gallery. */
  function studioUrl(o, way) {
    var c = ctx(o);
    if (way !== "blank" && way !== "upload") throw new Error("studioUrl: way must be blank|upload");
    if (!c.handle) return null;
    return c.storefront + "/products/" + enc(c.handle) + query([["variant", c.variantId], ["cfg", c.cfg], ["vvcc", way], ["vvmaster", c.master], ["vvfrom", "gallery"]]);
  }
  /* product page + ready-made design deep entry (?vvstyle=<registry key>). */
  function readyMadeUrl(o, key) {
    var c = ctx(o);
    if (!c.handle || !key) return null;
    return c.storefront + "/products/" + enc(c.handle) + query([["variant", c.variantId], ["cfg", c.cfg], ["vvstyle", String(key)], ["vvmaster", c.master], ["vvfrom", "gallery"]]);
  }
  /* product page, size not chosen (F4 CONFIG_REQUIRED recovery). designOnline=true adds the Path B continuation. */
  function needSizeUrl(o, designOnline) {
    var c = ctx(o);
    if (!c.handle) return null;
    return c.storefront + "/products/" + enc(c.handle) + query([["needsize", "1"], ["designOnline", designOnline ? "1" : ""]]);
  }
  /* the product page itself, with the chosen variant / calculator configuration restored and NO studio
     auto-launch parameter — where "Back to product" goes. */
  function productUrl(o) {
    var c = ctx(o);
    if (!c.handle) return null;
    return c.storefront + "/products/" + enc(c.handle) + query([["variant", c.variantId], ["cfg", c.cfg]]);
  }
  /* ITORIS custom-size product: hand-off to the product page's own configuration. */
  function productPageUrl(o) {
    var c = ctx(o);
    if (!c.handle) return null;
    return c.storefront + "/products/" + enc(c.handle) + query([["designOnline", "1"]]);
  }
  /* the design starting point — the recovery destination when no product can be resolved */
  function designOnlineUrl(o) { return ctx(o).storefront + "/pages/design-online"; }
  /* Release-hardening (2026-10-06): THE recovery destination for every Back / Close / Cancel / Try-again
     / error CTA on a modern journey. Never document.referrer, never history.back(): those can land on a
     product-page URL that auto-launches the legacy studio grid (Invariant 3). With a product + handle the
     customer returns to that product's gallery (same variant/config); with a handle only, to the product
     page asking for its size; with nothing, to the design starting point. */
  function recoveryUrl(o) {
    var c = ctx(o);
    if (c.productId && c.handle) return galleryUrl(c);
    if (c.handle) return needSizeUrl(c, true);
    return designOnlineUrl(c);
  }

  /* Read the contract back from a location.search (gallery.html / designer.html use this). */
  function parseEntry(search) {
    var qs; try { qs = new URLSearchParams(search || ""); } catch (e) { qs = { get: function () { return null; } }; }
    return {
      productId: digits(qs.get("product")),
      handle: handle(qs.get("gallery")),
      variantId: digits(qs.get("variant")),
      variantTitle: qs.get("variantTitle") || "",
      cfg: cfgToken(qs.get("cfg")),
      fromGallery: qs.get("from") === "gallery"
    };
  }

  var api = {
    TOOL: TOOL, STOREFRONT: STOREFRONT,
    galleryUrl: galleryUrl, designerUrl: designerUrl, studioUrl: studioUrl, readyMadeUrl: readyMadeUrl,
    needSizeUrl: needSizeUrl, productUrl: productUrl, productPageUrl: productPageUrl, designOnlineUrl: designOnlineUrl, recoveryUrl: recoveryUrl, parseEntry: parseEntry
  };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.VVDesignEntry = api;
})(typeof window !== "undefined" ? window : globalThis);
